/** Drawer "AI gợi ý" cho một bước BSC: timeline chuyên gia → diff từng mục (chọn/sửa) → Áp dụng / Hoàn tác. */
import React, { useState } from 'react';
import { Alert, Button, Drawer, List, Segmented, Space, Tabs, Tag, theme } from 'antd';
import type { AnyStepAdapter } from './adapters/types';
import { aiAgentApi } from '@/services/knowledge/ai-agent-api';
import type { AiConsistencyIssue } from '@/types/ai-agent';
import { gatherPlan, upstreamChange } from './consistency';
import { AiStepProposePanel } from './AiStepProposePanel';
import { useAiHubMe } from '@/components/common/AiHub/useAiHubMe';

const SEVERITY: Record<string, string> = { high: 'red', medium: 'orange', low: 'default' };

const ConsistencyPanel: React.FC<{ step: string }> = ({ step }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ issues: AiConsistencyIssue[]; summary: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const changed = upstreamChange(step);
  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const plan = await gatherPlan();
      const res = await aiAgentApi.consistency(plan);
      if (res?.success) setResult(res.data);
      else setError(res?.message || 'Không kiểm tra được');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Không kiểm tra được');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {changed && (
        <Alert type="warning" showIcon
          message={`Bước ${changed.step.toUpperCase()} vừa được thay đổi lúc ${new Date(changed.at).toLocaleString('vi-VN')} — nên kiểm tra lại tính nhất quán.`} />
      )}
      <Button type="primary" loading={loading} onClick={() => void run()}>Kiểm tra nhất quán B1 → B5</Button>
      {error && <Alert type="error" showIcon message={error} />}
      {result && (
        <>
          {result.summary && <Alert type="info" message={result.summary} />}
          <List
            dataSource={result.issues}
            locale={{ emptyText: 'Không phát hiện vấn đề.' }}
            renderItem={(i) => (
              <List.Item>
                <Space direction="vertical" size={2}>
                  <Space><Tag color={SEVERITY[i.severity]}>{i.severity}</Tag><Tag>{i.step.toUpperCase()}</Tag><span>{i.problem}</span></Space>
                  <span style={{ color: 'var(--ai-secondary)' }}>Gợi ý: {i.suggestion}</span>
                </Space>
              </List.Item>
            )}
          />
        </>
      )}
    </Space>
  );
};

export interface AiStepAssistantDrawerProps {
  open: boolean;
  onClose: () => void;
  adapters: AnyStepAdapter[];
  onApplied?: () => void;
  title?: string;
}

export const AiStepAssistantDrawer: React.FC<AiStepAssistantDrawerProps> = ({ open, onClose, adapters, onApplied, title }) => {
  const { token } = theme.useToken();
  const [key, setKey] = useState(adapters[0]?.key);
  const adapter = adapters.find((x) => x.key === key) ?? adapters[0];
  const { me } = useAiHubMe();
  const bubbleModes = me?.bubble_modes ?? {};
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={620}
      title={title ?? `AI gợi ý · ${adapter?.title ?? ''}`}
      destroyOnHidden
      styles={{
        body: {
          ['--ai-muted' as string]: token.colorFillAlter,
          ['--ai-border' as string]: token.colorBorderSecondary,
          ['--ai-secondary' as string]: token.colorTextSecondary,
          ['--ai-primary' as string]: token.colorPrimary,
          ['--ai-success' as string]: token.colorSuccess,
          ['--ai-error' as string]: token.colorError,
        },
      }}
    >
      {adapters.length > 1 && (
        <Segmented
          block
          style={{ marginBottom: 12 }}
          value={adapter.key}
          onChange={(v) => setKey(String(v))}
          options={adapters.map((x) => ({ value: x.key, label: x.title }))}
        />
      )}
      {adapter && (
        <Tabs
          items={[
            {
              key: 'propose',
              label: 'Đề xuất',
              children: (
                <AiStepProposePanel
                  key={adapter.key}
                  adapter={adapter}
                  onApplied={onApplied}
                  onClose={onClose}
                  // Module ở chế độ «đưa ra màn chính»: chỉ thêm vào trang (người dùng tự Lưu), không lưu thẳng
                  applyModes={bubbleModes[adapter.key.split('.')[0]] === 'write' ? ['append', 'save'] : ['append']}
                />
              ),
            },
            { key: 'check', label: 'Kiểm tra nhất quán', children: <ConsistencyPanel step={adapter.key.split('.')[0]} /> },
          ]}
        />
      )}
    </Drawer>
  );
};

export default AiStepAssistantDrawer;
